/**
 * Calendar Page
 * Day/Week/Month view calendar with appointment blocks.
 */
import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  addDays,
  subDays,
  addWeeks,
  subWeeks,
  addMonths,
  subMonths,
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
} from "date-fns";
import { de } from "date-fns/locale";
import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import {
  Button,
  Skeleton,
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@jourfx/ui";
import { useAuth } from "@/hooks/useAuth";
import { useQuery } from "convex/react";
import { api } from "@jourfx/convex/client";
import { WeekView } from "@/components/scheduling/calendar/WeekView";
import { DayView } from "@/components/scheduling/calendar/DayView";
import { MonthView } from "@/components/scheduling/calendar/MonthView";

export const Route = createFileRoute("/_authenticated/calendar/")({
  component: CalendarPage,
});

type ViewMode = "day" | "week" | "month";

function CalendarPage() {
  const { t } = useTranslation("calendar");
  const { user } = useAuth();
  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [view, setView] = useState<ViewMode>("week");
  const [selectedGutachterId, setSelectedGutachterId] = useState<string>("all");

  const isGutachter = user?.role === "gutachter";
  const gutachterList = useQuery(
    api.appointments.index.listGutachter,
    isGutachter ? "skip" : undefined
  );

  const activeGutachterId = isGutachter
    ? user?.id
    : selectedGutachterId === "all"
      ? undefined
      : selectedGutachterId;

  const periodLabel = useMemo(() => {
    if (view === "day") {
      return format(currentDate, "EEEE, dd. MMMM yyyy", { locale: de });
    }
    if (view === "week") {
      const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
      return `${format(weekStart, "dd.MM.yyyy", { locale: de })} – ${format(weekEnd, "dd.MM.yyyy", { locale: de })}`;
    }
    const monthStart = startOfMonth(currentDate);
    return `${format(monthStart, "MMMM yyyy", { locale: de })}`;
  }, [currentDate, view]);

  const goPrev = () => {
    if (view === "day") setCurrentDate(subDays(currentDate, 1));
    if (view === "week") setCurrentDate(subWeeks(currentDate, 1));
    if (view === "month") setCurrentDate(subMonths(currentDate, 1));
  };

  const goNext = () => {
    if (view === "day") setCurrentDate(addDays(currentDate, 1));
    if (view === "week") setCurrentDate(addWeeks(currentDate, 1));
    if (view === "month") setCurrentDate(addMonths(currentDate, 1));
  };

  return (
    <div className="space-y-4">
      {/* Page Header */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t("title")}</h1>
          <p className="text-muted-foreground">{t("subtitle")}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <Link to="/availability">
              <CalendarDays className="mr-2 h-4 w-4" />
              {t("availability")}
            </Link>
          </Button>
          <Button asChild>
            <Link to="/appointments/new">{t("newAppointment")}</Link>
          </Button>
        </div>
      </div>

      {/* Gutachter selector (Assistent) */}
      {!isGutachter && (
        <div className="max-w-sm">
          <label className="block text-sm font-medium mb-2">{t("selectExaminer")}</label>
          {gutachterList === undefined ? (
            <Skeleton className="h-9 w-full" />
          ) : (
            <Select
              value={selectedGutachterId}
              onValueChange={setSelectedGutachterId}
            >
              <SelectTrigger>
                <SelectValue placeholder={t("allExaminers")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allExaminers")}</SelectItem>
                {gutachterList.map((g: any) => (
                  <SelectItem key={g._id} value={g._id as string}>
                    {g.name} ({g.email})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}

      {/* Navigation + view toggle */}
      <div className="flex flex-wrap items-center justify-between gap-3" data-testid="week-navigation">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={goPrev} aria-label={t("prev")}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date())}>
            {t("today")}
          </Button>
          <Button variant="outline" size="icon" onClick={goNext} aria-label={t("next")}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium text-muted-foreground">{periodLabel}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant={view === "day" ? "default" : "outline"}
            onClick={() => setView("day")}
          >
            {t("day")}
          </Button>
          <Button
            size="sm"
            variant={view === "week" ? "default" : "outline"}
            onClick={() => setView("week")}
          >
            {t("week")}
          </Button>
          <Button
            size="sm"
            variant={view === "month" ? "default" : "outline"}
            onClick={() => setView("month")}
          >
            {t("month")}
          </Button>
        </div>
      </div>

      {/* Calendar grid */}
      {view === "day" && (
        <DayView
          currentDate={currentDate}
          gutachterId={activeGutachterId || undefined}
        />
      )}
      {view === "week" && (
        <WeekView
          currentDate={currentDate}
          gutachterId={activeGutachterId || undefined}
        />
      )}
      {view === "month" && (
        <MonthView
          currentDate={currentDate}
          gutachterId={activeGutachterId || undefined}
          onDateSelect={(date) => {
            setCurrentDate(date);
            setView("day");
          }}
        />
      )}
    </div>
  );
}
